import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { Theme } from "themes/interfaces";
import { Dark, Light } from "themes";
import { Auxiliars } from "helpers";
import { Store } from "utils";

type ThemeProps = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
};

interface Props {
  initTheme: Theme;
  children?: React.ReactNode;
}

const stored = Store.appStorage.get();

export const ThemeContext = createContext<ThemeProps>({
  theme: Light,
  setTheme: () => {},
  toggleTheme: () => {},
});

export const ThemeProvider = React.memo<Props>(
  ({ initTheme, children }: Props) => {
    const [theme, setTheme] = useState<Theme>(() => {
      if (stored.themeId === Dark.id) return Dark;
      if (stored.themeId === Light.id) return Light;
      return initTheme;
    });

    const setThemeCallback = useCallback((newTheme: Theme) => {
      setTheme((currentTheme: Theme) => {
        const merged = Auxiliars.deepInterpolation(currentTheme, newTheme);
        Store.appStorage.set({ themeId: merged.id });
        return merged;
      });
    }, []);

    const toggleThemeCallback = useCallback(() => {
      // Modern View Transitions API for zero-jank, native silky transitions if supported
      const applyNextTheme = () => {
        setTheme((currentTheme: Theme) => {
          const next = currentTheme.id === Dark.id ? Light : Dark;
          Store.appStorage.set({ themeId: next.id });
          return next;
        });
      };

      if (typeof document !== "undefined" && "startViewTransition" in document) {
        (document as any).startViewTransition(() => {
          applyNextTheme();
        });
      } else {
        applyNextTheme();
      }
    }, []);

    const value = useMemo(
      () => ({
        theme,
        setTheme: setThemeCallback,
        toggleTheme: toggleThemeCallback,
      }),
      [theme, setThemeCallback, toggleThemeCallback],
    );

    return (
      <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
  },
);

export const useTheme = () => useContext(ThemeContext);
